import type { FormattedAttachment } from "@productlane/ui"

const IMAGE_TYPES: Record<string, string> = {
	avif: "image/avif",
	gif: "image/gif",
	heic: "image/heic",
	jpeg: "image/jpeg",
	jpg: "image/jpeg",
	png: "image/png",
	svg: "image/svg+xml",
	webp: "image/webp",
}

const fileNameFromUrl = (src: string) => {
	const path = src.split("?")[0]?.split("#")[0] ?? src
	return decodeURIComponent(path.split("/").pop() ?? src)
}

const imageTypeOf = (name: string) => {
	const extension = name.includes(".")
		? (name.split(".").pop() ?? "").toLowerCase()
		: ""
	return IMAGE_TYPES[extension] ?? "image/png"
}

export interface MessageMedia {
	html: string
	videoUrls: string[]
	images: FormattedAttachment[]
}

/**
 * Splits stored media out of a message body so React renders it with the shared
 * player and attachment components instead of the raw markup the agent stored.
 */
export function extractMessageMedia(html: string): MessageMedia {
	if (typeof DOMParser === "undefined") {
		return { html, videoUrls: [], images: [] }
	}

	const doc = new DOMParser().parseFromString(html, "text/html")
	const videoUrls: string[] = []
	const images: FormattedAttachment[] = []

	doc.querySelectorAll("video[src]").forEach((video) => {
		const src = video.getAttribute("src")
		if (!src) return
		videoUrls.push(src)
		const paragraph = video.closest("p")
		if (paragraph && paragraph.querySelectorAll("video").length === 1) {
			paragraph.remove()
		} else {
			video.remove()
		}
	})

	doc.querySelectorAll("img[src]").forEach((image) => {
		const src = image.getAttribute("src")
		if (!src) return

		const paragraph = image.closest("p")
		if (!paragraph || paragraph.querySelectorAll("img").length !== 1) return

		const name = image.getAttribute("alt")?.trim() || fileNameFromUrl(src)
		const text = paragraph.textContent?.trim() ?? ""
		if (text !== "" && text !== name) return

		images.push({
			Content: src,
			Name: name,
			ContentType: imageTypeOf(name),
			ContentID: src,
		})
		paragraph.remove()
	})

	doc.querySelectorAll("p:not([class]):not([style])").forEach((paragraph) => {
		if (paragraph.innerHTML.trim() === "") paragraph.remove()
	})

	return { html: doc.body.innerHTML, videoUrls, images }
}
